import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, doc, deleteDoc, query, orderBy, updateDoc, serverTimestamp } from 'firebase/firestore';
import './AdminReports.css';

const AdminReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'approved', 'rejected'

  useEffect(() => {
    fetchReports();
  }, [filter]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      let reportsQuery;

      if (filter === 'all') {
        reportsQuery = query(collection(db, 'reports'), orderBy('createdAt', 'desc'));
      } else {
        reportsQuery = query(
          collection(db, 'reports'),
          orderBy('createdAt', 'desc')
        );
      }

      const querySnapshot = await getDocs(reportsQuery);
      const reportsList = [];

      querySnapshot.forEach((doc) => {
        const reportData = doc.data();
        reportsList.push({
          id: doc.id,
          ...reportData,
          createdAt: reportData.createdAt?.toDate().toLocaleString() || 'N/A',
          processedAt: reportData.processedAt?.toDate().toLocaleString() || 'N/A'
        });
      });

      // Filter reports based on status if a filter is selected
      const filteredReports = filter === 'all' 
        ? reportsList 
        : reportsList.filter(report => report.status === filter);

      setReports(filteredReports);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (window.confirm('Are you sure you want to delete this report? This action cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'reports', reportId));
        // Remove from local state
        setReports(reports.filter(report => report.id !== reportId));
        if (selectedReport && selectedReport.id === reportId) {
          setSelectedReport(null);
        }
      } catch (error) {
        console.error('Error deleting report:', error);
        alert('Failed to delete report. Please try again.');
      }
    }
  };

  const handleViewReport = (report) => {
    setSelectedReport(report);
  };

  const handleCloseDetails = () => {
    setSelectedReport(null);
  };

  const handleChangeStatus = async (reportId, newStatus) => {
    try {
      const reportRef = doc(db, 'reports', reportId);
      
      // Update the report status
      await updateDoc(reportRef, {
        status: newStatus,
        adminReviewedAt: serverTimestamp()
      });
      
      // If the report is approved, remove the content
      if (newStatus === 'approved') {
        const reportToUpdate = reports.find(report => report.id === reportId);
        if (reportToUpdate) {
          await handleContentRemoval(reportToUpdate);
        }
      }
      
      // Update in local state
      setReports(reports.map(report => 
        report.id === reportId ? { ...report, status: newStatus } : report
      ));
      
      if (selectedReport && selectedReport.id === reportId) {
        setSelectedReport({...selectedReport, status: newStatus});
      }
      
    } catch (error) {
      console.error('Error updating report status:', error);
      alert('Failed to update report status. Please try again.');
    }
  };

  // Function to handle content removal based on the type
  const handleContentRemoval = async (reportData) => {
    const { contentType, contentId, parentId, reportedUserId } = reportData;
    let queryId = null;
    
    try {
      switch (contentType) {
        case 'query':
          // Mark query as hidden/reported instead of deleting
          await updateDoc(doc(db, 'queries', contentId), {
            isHidden: true,
            reportReason: 'Violated community guidelines - Removed by admin',
            reportedAt: serverTimestamp()
          });
          queryId = contentId;
          break;
          
        case 'comment':
          // Mark comment as hidden/reported
          await updateDoc(doc(db, 'queries', parentId, 'comments', contentId), {
            isHidden: true,
            reportReason: 'Violated community guidelines - Removed by admin',
            reportedAt: serverTimestamp()
          });
          queryId = parentId;
          break;
          
        case 'reply':
          // Mark reply as hidden/reported
          const commentId = parentId; // For replies, parentId is the comment ID
          queryId = reportData.queryId; // We need to pass this in additionalData
          
          await updateDoc(doc(db, 'queries', queryId, 'comments', commentId, 'replies', contentId), {
            isHidden: true,
            reportReason: 'Violated community guidelines - Removed by admin',
            reportedAt: serverTimestamp()
          });
          break;
          
        default:
          console.error('Unknown content type:', contentType);
      }
      
      alert(`Content has been successfully hidden from view.`);
      return true;
    } catch (error) {
      console.error('Error handling content removal:', error);
      alert('Error removing content: ' + error.message);
      return false;
    }
  };

  // Function to get appropriate status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f8d64e'; // yellow
      case 'approved': return '#4caf50'; // green
      case 'rejected': return '#f44336'; // red
      default: return '#9e9e9e'; // grey
    }
  };

  return (
    <div className="admin-reports-container">
      <h2>Content Reports</h2>
      
      <div className="admin-review-banner">
        <div className="banner-icon">⚠️</div>
        <div className="banner-content">
          <h3>Human Review Required</h3>
          <p>All reports require human review, even if AI has made a recommendation. 
          Our community relies on your judgment to ensure content standards are maintained.</p>
        </div>
      </div>
      
      <div className="reports-controls">
        <div className="filter-controls">
          <label>Filter by status:</label>
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            className="status-filter"
          >
            <option value="all">All Reports</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <button onClick={fetchReports} className="refresh-button">
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="loading-spinner"></div>
      ) : reports.length === 0 ? (
        <p>No reports found.</p>
      ) : (
        <div className="reports-list-container">
          <table className="reports-table">
            <thead>
              <tr>
                <th>Report Date</th>
                <th>Content Type</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => (
                <tr key={report.id} onClick={() => handleViewReport(report)}>
                  <td>{report.createdAt}</td>
                  <td>{report.contentType}</td>
                  <td>{report.reason}</td>
                  <td>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(report.status) }}
                    >
                      {report.status}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewReport(report);
                      }}
                      className="view-button"
                    >
                      View
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteReport(report.id);
                      }}
                      className="delete-button"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedReport && (
        <div className="report-details-modal">
          <div className="report-details-content">
            <button onClick={handleCloseDetails} className="close-button">×</button>
            <h3>Report Details</h3>
            
            <div className="report-detail-row">
              <span className="detail-label">Status:</span>
              <span 
                className="status-badge"
                style={{ backgroundColor: getStatusColor(selectedReport.status) }}
              >
                {selectedReport.status}
              </span>
            </div>
            
            <div className="report-detail-row">
              <span className="detail-label">Content Type:</span>
              <span>{selectedReport.contentType}</span>
            </div>
            
            <div className="report-detail-row">
              <span className="detail-label">Reason:</span>
              <span>{selectedReport.reason}</span>
            </div>
            
            <div className="report-detail-row">
              <span className="detail-label">Reported Content:</span>
              <div className="reported-content">{selectedReport.reportedText}</div>
            </div>
            
            <div className="report-detail-row">
              <span className="detail-label">Reported By:</span>
              <span>{selectedReport.reporterId}</span>
            </div>
            
            <div className="report-detail-row">
              <span className="detail-label">Reported User:</span>
              <span>{selectedReport.reportedUserId}</span>
            </div>

            <div className="report-detail-row">
              <span className="detail-label">Created At:</span>
              <span>{selectedReport.createdAt}</span>
            </div>
            
            {selectedReport.aiAnalysis && (
              <>
                <h4>AI Analysis</h4>
                <div className="report-detail-row">
                  <span className="detail-label">Recommendation:</span>
                  <span>{selectedReport.aiAnalysis.recommendation}</span>
                </div>
                
                <div className="report-detail-row">
                  <span className="detail-label">Confidence:</span>
                  <span>{(selectedReport.aiAnalysis.confidence * 100).toFixed(1)}%</span>
                </div>
                
                <div className="report-detail-row">
                  <span className="detail-label">Reasoning:</span>
                  <span>{selectedReport.aiAnalysis.reasoning}</span>
                </div>
              </>
            )}
            
            <div className="report-actions">
              <h4>Actions</h4>
              <div className="status-buttons">
                <button 
                  onClick={() => handleChangeStatus(selectedReport.id, 'approved')}
                  className="status-button approve"
                  disabled={selectedReport.status === 'approved'}
                >
                  Approve & Remove Content
                </button>
                <button 
                  onClick={() => handleChangeStatus(selectedReport.id, 'rejected')}
                  className="status-button reject"
                  disabled={selectedReport.status === 'rejected'}
                >
                  Reject
                </button>
                <button 
                  onClick={() => handleDeleteReport(selectedReport.id)}
                  className="status-button delete"
                >
                  Delete Report
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReports; 